import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef, useReducer, ReactNode } from 'react';
import request from 'shared/lib/request';
import { fromByteArray } from 'base64-js';
import { v4 as uuidv4 } from 'uuid';
import ServerErrorHandler from 'shared/feedback/ErrorHandler';
import { type MessageKey, i18nMaybe } from 'shared/i18n';
import ConfirmationModal from 'shared/overlays/ConfirmationModal';

type FileZoneType = 'img' | 'xlsx' | 'xliff' | 'json' | 'font' | string;

interface TypeSettings {
  descriptionLabel: MessageKey | '-';
  mimetypes: string[];
}

interface StatusHandler {
  setIsLoading: (loading: boolean) => void;
  setError: (error: unknown) => void;
}

interface FileZoneOld {
  superId?: string;
}

interface FileZoneProps<TResult = unknown, TOld = FileZoneOld | null | undefined> {
  type: FileZoneType;
  documentId?: number | string;
  selectedOrganization?: number | string;
  onSuccess?: (result: TResult, old: TOld) => void;
  old?: FileZoneOld | null;
  url?: string;
  onProgress?: (progress: number) => void;
  onFileSelect?: (file: File) => void;
  statusHandler?: StatusHandler;
}

type FileZoneState = {
  dropDepth: number;
  isHovering: boolean;
};

type FileZoneAction = 'ENTERS' | 'LEAVES' | 'RESET';

const getTypeSettings = (type: FileZoneType): TypeSettings => {
  let descriptionLabel: MessageKey | '-';
  const mimetypes: string[] = [];

  switch (type) {
    case 'img':
      descriptionLabel = 'filezone:type.image';
      mimetypes.push('image/png', 'image/jpeg', 'image/webp', 'image/svg+xml');
      break;
    case 'xlsx':
      descriptionLabel = 'filezone:type.excel';
      mimetypes.push('application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');

      break;
    case 'xliff':
      descriptionLabel = 'filezone:type.xliff';
      // No mimemetypes for xliff, due to trouble selecting them with file picker
      break;
    case 'json':
      descriptionLabel = 'filezone:type.json';
      mimetypes.push('application/json', 'text/plain', 'application/zip');
      break;
    case 'font':
      descriptionLabel = 'filezone:type.font';
      mimetypes.push('font/ttf', 'font/woff', 'font/woff2', 'application/font-woff', 'font/otf');
      break;
    default:
      descriptionLabel = '-';
  }

  return { descriptionLabel, mimetypes };
};

const reducer = (state: FileZoneState, action: FileZoneAction): FileZoneState => {
  switch (action) {
    case 'ENTERS':
      return { dropDepth: state.dropDepth + 1, isHovering: state.dropDepth + 1 > 0 };
    case 'LEAVES':
      return { dropDepth: state.dropDepth - 1, isHovering: state.dropDepth - 1 > 0 };
    case 'RESET':
      return { dropDepth: 0, isHovering: false };
    default:
      return state;
  }
};

interface FileZoneTemplateProps {
  icon: string;
  title: ReactNode;
  subtitle?: ReactNode;
  details?: ReactNode;
  errorMessage?: ReactNode;
  isLoading?: boolean;
  isActive?: boolean;
}

export const FileZoneTemplate = ({ icon, title, subtitle, details, errorMessage, isLoading, isActive }: FileZoneTemplateProps) => (
  <div
    className={`cursor-pointer border-dashed border-1 ${
      isActive ? 'border-blue-800' : 'border-blue-400'
    } bg-blue-200 px-3 py-4 rounded-3 d-flex align-items-center justify-content-center flex-column text-gray-800 text-center h-100`}
  >
    <span className="text-blue-800" style={{ fontSize: '2.7rem' }}>
      <i className={isLoading ? 'fa-regular fa-circle-notch fa-spin' : icon} />
    </span>
    <h1 className="fs-7 fw-light">{title}</h1>
    <p className="fs-7 text-gray-700 mb-0">{subtitle}</p>
    <p className="fs-7 text-gray-700 mb-0">{details}</p>
    {errorMessage ? <p className="text-danger fs-7 mb-0">{errorMessage}</p> : null}
  </div>
);

const FileZone = <TResult = unknown, TOld = FileZoneOld | null | undefined>(props: FileZoneProps<TResult, TOld>) => {
  const { t } = useTranslation('filezone');

  const { type, documentId, selectedOrganization, onSuccess, old, url = `/gaby/files`, onProgress, onFileSelect } = props;
  const [processFile, setProcessFile] = useState(false);
  const [isUnsupported, setIsUnsupported] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [{ isHovering }, addEvent] = useReducer(reducer, { dropDepth: 0, isHovering: false });

  useEffect(() => {
    if (isHovering) {
      setIsUnsupported(false);
    }
  }, [isHovering]);

  const ref = useRef<HTMLInputElement>(null);
  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    addEvent('ENTERS');
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    addEvent('LEAVES');
  };

  const onFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    addEvent('RESET');

    handleFileUpload(e.dataTransfer.files[0]);
  };

  const handleConfirmation = () => {
    setShowConfirmationModal(true);
  };

  const handleConfirmUpload = () => {
    setShowConfirmationModal(false);
    if (selectedFile) {
      handleFileUpload(selectedFile);
    }
  };

  const handleCancelUpload = () => {
    setShowConfirmationModal(false);
  };

  const handleFileUpload = (file: File | undefined) => {
    // Guard against empty/folder drops where dataTransfer.files[0] is undefined.
    if (!file) {
      setIsUnsupported(true);
      return;
    }

    const { mimetypes } = getTypeSettings(type);
    props.statusHandler?.setIsLoading(true);
    props.statusHandler?.setError(false);
    if (mimetypes.length && !mimetypes.includes(file.type)) {
      setIsUnsupported(true);
      props.statusHandler?.setIsLoading(false);
      return;
    }

    setProcessFile(true);

    file
      .arrayBuffer()
      .then((buffer) => {
        const body = {
          superId: old?.superId || uuidv4(),
          revisionId: uuidv4(),
          documentSuperId: documentId,
          organization: selectedOrganization,
          filename: file.name,
          fileType: file.type,
          fileSize: file.size,
          fileContent: fromByteArray(new Uint8Array(buffer)),
          type: type,
        };

        return request.post(url, body, null, onProgress);
      })
      .then((result) => {
        props.statusHandler?.setIsLoading(false);
        props.statusHandler?.setError(false);
        const emitSuccess = onSuccess as ((result: unknown, old: unknown) => void) | undefined;
        emitSuccess?.(result, old);
      })
      .catch((e: unknown) => {
        props.statusHandler?.setIsLoading(false);
        props.statusHandler?.setError(e);
        return ServerErrorHandler(e);
      })
      .finally(() => {
        // Always clear the spinner, regardless of success or failure.
        setProcessFile(false);
        setSelectedFile(null);
      });
  };

  const handleClick = () => {
    setIsUnsupported(false);
    if (ref.current && showConfirmationModal === false) {
      ref.current.click();
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    onFileSelect?.(file);
    if (file.size > 2048000) {
      setSelectedFile(file);
      handleConfirmation();
    } else {
      handleFileUpload(file);
    }
  };

  const { descriptionLabel, mimetypes } = getTypeSettings(type);

  return (
    <div onClick={handleClick} onDragEnter={onDragEnter} onDragOver={onDragOver} onDrop={onFileDrop} onDragLeave={onDragLeave}>
      <FileZoneTemplate
        icon={isHovering ? 'fa-solid fa-folder-plus' : 'fa-regular fa-folder-plus'}
        isActive={isHovering}
        isLoading={processFile}
        title={t('title')}
        subtitle={t('subtitle')}
        details={i18nMaybe(descriptionLabel)}
        errorMessage={isUnsupported ? t('filedrop.unsupported') : null}
      />
      <ConfirmationModal
        message={t('size.warning')}
        show={showConfirmationModal}
        onConfirm={() => handleConfirmUpload()}
        onDecline={() => handleCancelUpload()}
      />
      <input onChange={handleFileInput} ref={ref} className="d-none" type="file" accept={mimetypes.join(',')} />
    </div>
  );
};

export default FileZone;
